'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { CustomDomainItem, useCustomDomainsList, useDomainConfig, useVerifyCustomDomain } from '@/lib/api/domains'
import { formatDate } from '@/lib/format'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle } from 'lucide-react'

interface DomainDetailModalProps {
	domain: CustomDomainItem | null
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function DomainDetailModal({ domain, open, onOpenChange }: DomainDetailModalProps) {
	const { t } = useTranslation('domains')

	const { data: domainConfig, isLoading: configLoading, refetch: refetchConfig } = useDomainConfig(
		{ domain: domain?.domain || '' },
		!!domain?.domain
	)
	const { mutate: verifyDomain, isPending: isVerifyingDomain } = useVerifyCustomDomain()
	const { refetch: refetchDomains, isLoading: isLoadingDomains } = useCustomDomainsList()

	const handleRefresh = () => {
		verifyDomain({ domainId: domain?.id || '' }, {
			onSuccess: () => {
				refetchDomains()
				refetchConfig()
			}
		})
	}

	if (!domain) return null

	const mainDnsRecord = domainConfig?.misconfigured
		? domainConfig.isApex
			? {
					type: 'A' as const,
					name: '@',
					value: domainConfig.recommendedIPv4?.[0] ?? '76.76.21.21',
				}
			: {
					type: 'CNAME' as const,
					name: domainConfig.cnameHost,
					value: domainConfig.recommendedCNAME,
				}
		: null

	const conflictRecords = domainConfig?.conflicts ?? []
	const hasConflicts = conflictRecords.length > 0

	const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
		<div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
			<span className="text-sm font-medium">{label}:</span>
			<span className="sm:col-span-3">{children}</span>
		</div>
	)

	const isLoading = isLoadingDomains || configLoading || isVerifyingDomain
	const hasRequiredRecords = domainConfig && (
		domainConfig.misconfigured ||
		!domainConfig.verified ||
		(domainConfig.verificationChallenges?.length ?? 0) > 0 ||
		hasConflicts
	)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="mx-4 sm:max-w-[500px] md:max-w-[600px]">
				<DialogHeader>
					<DialogTitle>{t('modal.title')}</DialogTitle>
				</DialogHeader>
				<div className="grid gap-3 py-3 sm:gap-4 sm:py-4">
					<DetailRow label={t('modal.fields.domain')}>
						<span className="break-all font-medium">{domain.domain}</span>
					</DetailRow>
					<DetailRow label={t('modal.fields.id')}>
						<CopyableResourceBadge
							name={t('modal.fields.id')}
							value={domain.id}
							variant="secondary"
						/>
					</DetailRow>
					<DetailRow label={t('modal.fields.verificationStatus')}>
						<Badge variant={domain.isVerified ? 'green' : 'yellow'}>
							{domain.isVerified ? t('table.status.verified') : t('table.status.pending')}
						</Badge>
					</DetailRow>
					<DetailRow label={t('modal.fields.status')}>
						<Badge variant={domain.enabled ? 'green' : 'secondary'}>
							{domain.enabled ? t('table.status.active') : t('table.status.inactive')}
						</Badge>
					</DetailRow>
					{domain.verifiedAt && (
						<DetailRow label={t('modal.fields.verifiedAt')}>
							<span className="text-sm">
								{formatDate(new Date(domain.verifiedAt), 'MMM d, yyyy h:mm a')}
							</span>
						</DetailRow>
					)}
					<DetailRow label={t('modal.fields.createdAt')}>
						<span className="text-sm">
							{formatDate(new Date(domain.createdAt), 'MMM d, yyyy h:mm a')}
						</span>
					</DetailRow>
					<DetailRow label={t('modal.fields.updatedAt')}>
						<span className="text-sm">
							{formatDate(new Date(domain.updatedAt), 'MMM d, yyyy h:mm a')}
						</span>
					</DetailRow>
				</div>

				{/* DNS Configuration */}
				<div className="border-t pt-4 mt-4">
					{configLoading ? (
						<>
							<h3 className="text-sm font-semibold mb-3">{t('modal.dnsRecord.title')}</h3>
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-[80px]">{t('modal.dnsRecord.type')}</TableHead>
											<TableHead className="w-[120px]">{t('modal.dnsRecord.name')}</TableHead>
											<TableHead>{t('modal.dnsRecord.value')}</TableHead>
											<TableHead className="w-[80px]">{t('modal.dnsRecord.ttl')}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										<TableRow>
											<TableCell><Skeleton className="h-4 w-12" /></TableCell>
											<TableCell><Skeleton className="h-4 w-8" /></TableCell>
											<TableCell><Skeleton className="h-6 w-32" /></TableCell>
											<TableCell><Skeleton className="h-4 w-12" /></TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</div>
						</>
					) : hasRequiredRecords ? (
						<>
							<div className="flex justify-between items-center">
								<h3 className="text-sm font-semibold mb-3">{t('modal.dnsRecord.titleRequired')}</h3>
								<Button variant="outline" className="mb-3" onClick={handleRefresh} disabled={isLoading}>
									{t('modal.fields.refresh')}
								</Button>
							</div>
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-[80px]">{t('modal.dnsRecord.type')}</TableHead>
											<TableHead className="w-[120px]">{t('modal.dnsRecord.name')}</TableHead>
											<TableHead>{t('modal.dnsRecord.value')}</TableHead>
											<TableHead className="w-[80px]">{t('modal.dnsRecord.ttl')}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{/* Main DNS record — A @ IPv4 for apex, CNAME for subdomains (#TAG-1735) */}
										{mainDnsRecord && (
											<TableRow>
												<TableCell className="font-mono text-xs">{mainDnsRecord.type}</TableCell>
												<TableCell className="font-mono text-xs">{mainDnsRecord.name}</TableCell>
												<TableCell className="max-w-[200px]">
													<CopyableResourceBadge
														name={mainDnsRecord.type}
														value={mainDnsRecord.value}
														variant="secondary"
														className="truncate max-w-full"
													/>
												</TableCell>
												<TableCell className="font-mono text-xs">3600</TableCell>
											</TableRow>
										)}
										{/* Vercel verification challenges */}
										{domainConfig.verificationChallenges?.map((challenge: any, idx: number) => {
											const truncatedValue = challenge.value.length > 20
												? `${challenge.value.slice(0, 20)}...`
												: challenge.value
											return (
												<TableRow key={idx}>
													<TableCell className="font-mono text-xs">{challenge.type}</TableCell>
													<TableCell className="font-mono text-xs break-all">{challenge.domain}</TableCell>
													<TableCell className="max-w-[200px]">
														<CopyableResourceBadge
															name={challenge.type}
															value={truncatedValue}
															copyValue={challenge.value}
															variant="secondary"
														/>
													</TableCell>
													<TableCell className="font-mono text-xs">3600</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							</div>
							{hasConflicts && (
								<div className="mt-4">
									<h3 className="text-sm font-semibold mb-1 text-destructive flex items-center gap-1.5">
										<AlertTriangle className="h-4 w-4" aria-hidden="true" />
										{t('modal.dnsRecord.titleConflicts')}
									</h3>
									<p className="text-xs text-muted-foreground mb-2">
										{t('modal.dnsRecord.conflictsDescription')}
									</p>
									<div className="rounded-md border border-destructive/40">
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead className="w-[80px]">{t('modal.dnsRecord.type')}</TableHead>
													<TableHead className="w-[120px]">{t('modal.dnsRecord.name')}</TableHead>
													<TableHead>{t('modal.dnsRecord.value')}</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{conflictRecords.map((conflict, idx: number) => (
													<TableRow key={`conflict-${idx}`}>
														<TableCell className="font-mono text-xs">{conflict.type}</TableCell>
														<TableCell className="font-mono text-xs break-all">{conflict.name}</TableCell>
														<TableCell className="max-w-[200px]">
															<CopyableResourceBadge
																name={conflict.type}
																value={conflict.value}
																variant="secondary"
																className="truncate max-w-full"
															/>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								</div>
							)}
						</>
					) : domainConfig ? (
						<>
							<div className="flex justify-between items-center">
								<h3 className="text-sm font-semibold mb-1">{t('modal.dnsRecord.title')}</h3>
								<Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
									{t('modal.fields.refresh')}
								</Button>
							</div>
							<p className="text-sm text-muted-foreground">
								{t('modal.dns.allGood')}
							</p>
						</>
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	)
}

